/**
 * 账户设置页：账户信息与 API Key。
 * API Key 默认脱敏显示，可切换查看并一键复制完整值。
 */

import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  RotateCcw,
  User,
} from 'lucide-react';
import { api } from '../../services/api';
import { SectionCard } from './shared';

export default function SettingsPage() {
  const [user, setUser] = useState<{ email: string; name: string | null; apiKey: string } | null>(
    null
  );
  const [keyVisible, setKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const saved = api.getSavedUser();
    if (saved) {
      setUser({ email: saved.email, name: saved.name, apiKey: saved.apiKey });
    }
  }, []);

  const handleCopy = async () => {
    if (!user?.apiKey) return;
    try {
      await navigator.clipboard.writeText(user.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('复制失败，请手动选择内容复制');
    }
  };

  const handleRefresh = async () => {
    const confirmed = window.confirm(
      '确定要重新生成 API Key 吗？旧的 Key 会立即失效，正在使用旧 Key 的服务需要同步更新。'
    );
    if (!confirmed) return;

    setRefreshing(true);
    setError('');
    setNotice('');
    try {
      const data = await api.refreshApiKey();
      setUser((prev) => (prev ? { ...prev, apiKey: data.apiKey } : prev));
      setKeyVisible(true);
      setNotice('新的 API Key 已生成，请及时更新调用配置');
    } catch (err) {
      setError(err instanceof Error ? err.message : '重新生成失败');
    } finally {
      setRefreshing(false);
    }
  };

  const maskedKey = user?.apiKey
    ? `${user.apiKey.slice(0, 6)}${'•'.repeat(18)}${user.apiKey.slice(-4)}`
    : '';

  return (
    <>
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <SectionCard title="账户信息" bodyClassName="divide-y divide-light-border">
        <div className="px-5 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-light-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-light-muted">昵称</p>
            <p className="text-sm text-light-text mt-0.5 truncate">{user?.name || '未设置'}</p>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-light-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-light-muted">邮箱</p>
            <p className="text-sm text-light-text mt-0.5 truncate">{user?.email || '—'}</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="API Key"
        subtitle="用于通过接口调用生成服务，请勿分享给他人"
        bodyClassName="p-5"
      >
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-light-hover flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-light-secondary" />
          </div>
          <div className="flex-1 min-w-0 bg-light-bg border border-light-border rounded-xl px-3 py-2.5 font-mono text-xs sm:text-sm text-light-text break-all">
            {keyVisible ? user?.apiKey : maskedKey || '—'}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setKeyVisible((prev) => !prev)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-light-border rounded-xl text-xs text-light-secondary hover:bg-light-hover transition-colors"
          >
            {keyVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {keyVisible ? '隐藏' : '显示完整 Key'}
          </button>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-accent-blue text-white rounded-xl text-xs hover:opacity-90 transition-opacity"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? '已复制' : '复制 Key'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-light-border rounded-xl text-xs text-light-secondary hover:bg-light-hover transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? '生成中...' : '重新生成'}
          </button>
        </div>

        {notice && (
          <p className="mt-3 text-xs text-green-600 flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" />
            {notice}
          </p>
        )}

        <p className="mt-3 text-xs text-light-muted leading-relaxed">
          重新生成后旧 Key 立即失效，且无法恢复；正在使用旧 Key 的脚本或第三方服务需要同步替换。
        </p>
      </SectionCard>
    </>
  );
}
